import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { paymentAPI } from '../lib/api';
import { toast } from 'sonner';

export function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking, success, failed
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    const pollStatus = async () => {
      try {
        const response = await paymentAPI.getStatus(sessionId);
        
        if (response.data.payment_status === 'paid') {
          setStatus('success');
          toast.success('Payment successful! Thank you for your support.');
        } else if (response.data.status === 'expired' || response.data.payment_status === 'unpaid') {
          setStatus('failed');
        } else if (attempts < 5) {
          // Continue polling
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      } catch (error) {
        console.error('Payment status check failed:', error);
        if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('failed');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-orange-500 animate-spin mb-6" />
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Processing Payment
            </h1>
            <p className="text-slate-600 mb-8">
              Please wait while we confirm your payment...
            </p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Thank You!
            </h1>
            <p className="text-slate-600 mb-8">
              Your micro-grant has been received. You're helping build momentum for a great idea!
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/discover')}
                className="w-full btn-boost"
                data-testid="continue-btn"
              >
                Continue Exploring
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Link to="/leaderboard">
                <Button variant="outline" className="w-full rounded-full">
                  View Leaderboard
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-2">
              Payment Issue
            </h1>
            <p className="text-slate-600 mb-8">
              We couldn't confirm your payment. If you were charged, please contact support.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/discover')}
                className="w-full"
              >
                Back to Discover
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
